import { useCallback, useEffect, useRef, useState } from 'react';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import { ISpeechToTextModel } from 'generated/search-backend';

type SpeechTokenResult = ISpeechToTextModel | { data?: ISpeechToTextModel } | void;
export type SpeechTokenFetcher = () => SpeechTokenResult | Promise<SpeechTokenResult>;

const getStoredToken = (): ISpeechToTextModel | undefined => {
    const storedToken = sessionStorage.getItem('speech-token');
    if (!storedToken) return undefined;

    try {
        const parsed: unknown = JSON.parse(storedToken);
        if (typeof parsed !== 'object' || parsed === null) return undefined;
        const stored = parsed as Record<string, unknown>;
        const token = stored.token ?? stored.Token;
        const region = stored.region ?? stored.Region;
        if (typeof token !== 'string' || typeof region !== 'string') return undefined;
        return { token, region };
    } catch {
        return undefined;
    }
};

const unwrapToken = (result: SpeechTokenResult): ISpeechToTextModel | undefined => {
    if (!result) return undefined;
    const wrappedResult = result as { data?: ISpeechToTextModel };
    return wrappedResult.data ?? (result as ISpeechToTextModel);
};

export function useSpeechRecognition(
    getSpeechToTextToken: SpeechTokenFetcher | undefined,
    onRecognized: (text: string) => void,
    initialToken?: ISpeechToTextModel,
) {
    const [isRecording, setIsRecording] = useState(false);
    const [speechError, setSpeechError] = useState('');
    const recognizerRef = useRef<speechSdk.SpeechRecognizer | null>(null);
    const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startIdRef = useRef(0);
    const isStartingRef = useRef(false);

    const stop = useCallback(() => {
        startIdRef.current += 1;
        isStartingRef.current = false;
        if (stopTimerRef.current) {
            clearTimeout(stopTimerRef.current);
            stopTimerRef.current = null;
        }
        const recognizer = recognizerRef.current;
        recognizerRef.current = null;
        setIsRecording(false);
        if (recognizer) {
            recognizer.stopContinuousRecognitionAsync(
                () => recognizer.close(),
                () => recognizer.close(),
            );
        }
    }, []);

    useEffect(() => stop, [stop]);

    const start = useCallback(async () => {
        const startId = startIdRef.current + 1;
        startIdRef.current = startId;
        isStartingRef.current = true;
        setSpeechError('');
        try {
            const fetched = getSpeechToTextToken ? unwrapToken(await getSpeechToTextToken()) : undefined;
            if (startId !== startIdRef.current) return;
            const token = fetched?.token ? fetched : initialToken?.token ? initialToken : getStoredToken();
            if (!token?.token || !token.region) {
                throw new Error('Speech token unavailable');
            }

            const speechConfig = speechSdk.SpeechConfig.fromAuthorizationToken(token.token, token.region);
            speechConfig.speechRecognitionLanguage = 'en-US';
            const audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
            const recognizer = new speechSdk.SpeechRecognizer(speechConfig, audioConfig);
            if (startId !== startIdRef.current) {
                recognizer.close();
                return;
            }
            recognizerRef.current = recognizer;
            isStartingRef.current = false;
            recognizer.recognized = (_, event) => {
                const text = event.result.text?.trim();
                if (text) {
                    onRecognized(text.replace(/^SKP\s+/i, 'SKP-'));
                    stop();
                }
            };
            recognizer.canceled = () => {
                setSpeechError('Voice search is unavailable. Please type your search.');
                stop();
            };
            recognizer.startContinuousRecognitionAsync(
                () => {
                    if (startId !== startIdRef.current) {
                        recognizer.close();
                        return;
                    }
                    setIsRecording(true);
                    stopTimerRef.current = setTimeout(stop, 20000);
                },
                () => {
                    if (startId !== startIdRef.current) return;
                    setSpeechError('Voice search is unavailable. Please type your search.');
                    stop();
                },
            );
        } catch {
            if (startId !== startIdRef.current) return;
            setSpeechError('Voice search is unavailable. Please type your search.');
            stop();
        }
    }, [getSpeechToTextToken, initialToken, onRecognized, stop]);

    const toggle = useCallback(() => {
        if (isStartingRef.current || recognizerRef.current || isRecording) {
            stop();
        } else {
            void start();
        }
    }, [isRecording, start, stop]);

    return { isRecording, speechError, toggle };
}
